import React, { memo, useCallback, useState, useRef } from 'react';
import { Card, CardContent, Box, Typography, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, IconButton, Collapse } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { reportStyles, reportCommonStyles } from '../../../shared/globalSX';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import { Skeleton } from '@mui/material';
import { COLORS } from '../../../shared/colors';
import useCloseOnScroll from '../../../shared/useCloseOnScroll';

const DueDateTable = memo(({ dueDateFilter, setDueDateFilter, dueDateData, defaultExpanded = true, canCollapse = true, isLoading = false }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [activeTitleTooltip, setActiveTitleTooltip] = useState(null);
  const tableContainerRef = useRef(null);

  const closeTitleTooltip = useCallback(() => {
    setActiveTitleTooltip(null);
  }, []);

  useCloseOnScroll(tableContainerRef, closeTitleTooltip);

  // If collapse is disabled, ensure it stays expanded
  const expanded = canCollapse ? isExpanded : true;

  return (
    <Card className="report-table-card" sx={{ minHeight: expanded ? 480 : 'auto', maxHeight: expanded ? 480 : 'auto', height: '100%', overflow: 'hidden', transition: 'all 0.3s' }}>
      <CardContent sx={reportCommonStyles.cardPadding}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          pb={1.5}
          mb={expanded ? 2 : 0}
          sx={{ borderBottom: `1px solid ${COLORS.brand.primaryDark}` }}
        >
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1} 
            sx={{ cursor: canCollapse ? 'pointer' : 'default' }} 
            onClick={() => canCollapse && setIsExpanded(!isExpanded)}
          >
            {canCollapse && (
              <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                {isExpanded ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
              </IconButton>
            )}
            <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.brand.primaryDark }}>{t('reports.upcomingDueDates.title')}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Box display="flex" gap={1}>
              <Button
                variant={dueDateFilter === 'overdue' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setDueDateFilter('overdue')}
                sx={{
                  ...reportStyles.buttonSmall,
                  ...(dueDateFilter === 'overdue' ? reportStyles.buttonContained : reportStyles.buttonOutlined)
                }}
              >
                {t('reports.upcomingDueDates.overdue')}
              </Button>
              <Button
                variant={dueDateFilter === 'today' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setDueDateFilter('today')}
                sx={{
                  ...reportStyles.buttonSmall,
                  ...(dueDateFilter === 'today' ? reportStyles.buttonContained : reportStyles.buttonOutlined)
                }}
              >
                {t('reports.upcomingDueDates.dueToday')}
              </Button>
              <Button
                variant={dueDateFilter === 'week' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setDueDateFilter('week')}
                sx={{
                  ...reportStyles.buttonSmall,
                  ...(dueDateFilter === 'week' ? reportStyles.buttonContained : reportStyles.buttonOutlined)
                }}
              >
                {t('reports.upcomingDueDates.dueThisWeek')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {(isLoading || dueDateData === null) ? (
               <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...Array(6)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="rounded" height={24} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
               </TableContainer>
            ) : (!dueDateData || dueDateData.length === 0) ? (
              <Box sx={reportStyles.noDataContainer}>
                <Typography variant="body2" color="text.secondary">{t("reports.noDataAvailable")}</Typography>
              </Box>
            ) : (
              <TableContainer ref={tableContainerRef} className="report-table-container report-due-date-table-container">
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    '& th:first-of-type, & td:first-of-type': { pl: 1.5 },
                    '& th:nth-of-type(2), & td:nth-of-type(2), & th:nth-of-type(3), & td:nth-of-type(3)': { textAlign: 'center' },
                    '& th:last-of-type, & td:last-of-type': { pr: 1.5, textAlign: 'right' },
                    '& th, & td': { px: 1.5 }
                  }}
                >
                  <colgroup>
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={reportStyles.tableHeaderCell}>{t('reports.upcomingDueDates.tableHeaders.title')}</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>{t('reports.upcomingDueDates.tableHeaders.assignee')}</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>{t('reports.upcomingDueDates.tableHeaders.startDate')}</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>{t('reports.upcomingDueDates.tableHeaders.dueDate')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dueDateData.map((row) => (
                      <TableRow
                        key={row.ticket_number}
                        hover
                        onClick={() => window.open(`/task/${row.ticket_prefix}-${row.ticket_number}`, '_blank')}
                        sx={{
                          ...reportStyles.hoverRow,
                          '&:nth-of-type(even)': {
                            bgcolor: '#EAEFFC'
                          }
                        }}
                      >
                        <TableCell sx={reportCommonStyles.tableCellMaxWidth200}>
                          {row.title.length > 30 ? (
                            <Tooltip
                              title={row.title}
                              arrow
                              open={activeTitleTooltip === row.ticket_number}
                              disableHoverListener
                              disableFocusListener
                              disableTouchListener
                              disableInteractive
                              onClose={closeTitleTooltip}
                              TransitionProps={{ timeout: 0 }}
                              PopperProps={{
                                disablePortal: true,
                                modifiers: [
                                  {
                                    name: 'preventOverflow',
                                    options: {
                                      boundary: 'clippingParents',
                                      altBoundary: true,
                                      padding: 8,
                                    },
                                  },
                                  {
                                    name: 'flip',
                                    options: {
                                      fallbackPlacements: ['top-start', 'bottom-start'],
                                      padding: 8,
                                    },
                                  },
                                  {
                                    name: 'offset',
                                    options: {
                                      offset: [0, 8],
                                    },
                                  },
                                ],
                              }}
                              classes={{ tooltip: 'app-tooltip' }}
                            >
                              <Box
                                onMouseEnter={() => {
                                  setActiveTitleTooltip(row.ticket_number);
                                }}
                                onMouseLeave={closeTitleTooltip}
                              >
                                <Box sx={reportStyles.textEllipsis}>
                                  {row.title}
                                </Box>
                                <Typography variant="caption" sx={reportStyles.captionText}>
                                  {row.ticket_prefix}-{row.ticket_number}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box>
                              <Box sx={reportStyles.textEllipsis}>
                                {row.title}
                              </Box>
                              <Typography variant="caption" sx={reportStyles.captionText}>
                                {row.ticket_prefix}-{row.ticket_number}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...reportCommonStyles.tableCellMaxWidth150, '& > *': { justifyContent: 'center' } }}>
                          <AssigneeAvatarStack assignees={row.assignees} assignee={row.assignee_name} />
                        </TableCell>
                        <TableCell sx={reportCommonStyles.tableCellMaxWidth200}>{row.start_date ? new Date(row.start_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell
                          sx={{
                            ...reportCommonStyles.tableCellMaxWidth200,
                            color: new Date(row.due_date) < new Date() ? COLORS.red[600] : COLORS.brand.primaryDark,
                            fontWeight: 500
                          }}
                        >
                          {new Date(row.due_date).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) =>
  prevProps.dueDateFilter === nextProps.dueDateFilter &&
  prevProps.dueDateData === nextProps.dueDateData &&
  prevProps.isLoading === nextProps.isLoading &&
  prevProps.defaultExpanded === nextProps.defaultExpanded &&
  prevProps.canCollapse === nextProps.canCollapse
);

export default DueDateTable;
